import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { SERVICES } from "@/lib/constants";
import { toast } from "sonner";
import { Camera, Monitor, Laptop, Printer, ShieldCheck, Network, CheckCircle2 } from "lucide-react";

const ICONS = { camera: Camera, monitor: Monitor, laptop: Laptop, printer: Printer, "shield-check": ShieldCheck, network: Network };

export default function Services() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: SERVICES[0].title, address: "", preferred_date: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const h = window.location.hash?.slice(1);
    if (h) {
      const el = document.getElementById(h);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/service-request", form);
      toast.success("Service request received. We'll call you shortly.");
      setForm({ ...form, message: "", address: "", preferred_date: "" });
    } catch { toast.error("Failed to submit"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section className="bg-slate-950 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-sm uppercase tracking-widest text-blue-300 font-semibold mb-2">Services</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-balance">On-Site Technology Services</h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">CCTV installation, computer & printer repair, AMC contracts, business IT — one team, one number.</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon] || Monitor;
            return (
              <div id={s.slug} key={s.slug} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-soft hover:shadow-lift hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 grid place-items-center rounded-xl bg-blue-900 text-white mb-5"><Icon className="w-5 h-5" /></div>
                <h3 className="font-heading font-semibold text-xl mb-2 text-slate-900">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{s.desc}</p>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> Free on-site inspection</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> Certified technicians</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> Transparent pricing</li>
                </ul>
                <button onClick={() => { setForm({ ...form, service_type: s.title }); document.getElementById("service-form")?.scrollIntoView({ behavior: "smooth" }); }} className="mt-6 text-sm font-medium text-blue-900 hover:text-blue-700" data-testid={`service-book-${s.slug}`}>Book this service →</button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50" id="service-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-sm uppercase tracking-widest text-blue-700 font-semibold mb-2">Book a Service</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold">Request Technician Visit</h2>
          </div>
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-8 space-y-4 shadow-soft">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="srv-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="srv-email" />
              <input required placeholder="Phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="srv-phone" />
              <select value={form.service_type} onChange={(e)=>setForm({...form,service_type:e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="srv-type">
                {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
              </select>
              <input placeholder="Address" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="srv-address" />
              <input type="date" placeholder="Preferred date" value={form.preferred_date} onChange={(e)=>setForm({...form,preferred_date:e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="srv-date" />
            </div>
            <textarea required rows={4} placeholder="Describe the issue or requirement..." value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="srv-message"></textarea>
            <button disabled={busy} className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3 rounded-xl font-medium disabled:opacity-60" data-testid="srv-submit">
              {busy ? "Submitting..." : "Request Service"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
